import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Image } from '@/components/common/image';
import { HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { SliderV2 } from '@/components/common/SliderCommon/SliderV2';
import { Tag } from '@/components/common/Tag';
import { Section } from '@/components/layout/Section';

import type { SanityImageTextColumnsType } from '@/types/sanity';

export interface ImageTextColumnsProps extends SanityImageTextColumnsType {}

/**
Component accepts 1-3 items only.

When this is used for the coaches, the image being used is a png with white space around the circle.
Download the image first so that you can replicate this style accurately."
*/

const ImageTextColumns = ({
  tag,
  caption,
  heading,
  subheading,
  columns,
  backgroundColour,
}: ImageTextColumnsProps) => {
  const sliderSettings = {
    dots: true,
    speed: 500,
    slidesToScroll: 1,
    infinite: false,
    rows: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 640,
        settings: {
          rows: 1,
          slidesToShow: 1,
          dots: columns.length > 1,
          centerMode: true,
          centerPadding: '20px',
        },
      },
      {
        breakpoint: 976,
        settings: {
          centerMode: false,
          rows: 1,
          slidesToShow: columns.length > 2 ? 2 : columns.length,
          initialSlide: 1,
          dots: columns.length > 2,
        },
      },
      {
        breakpoint: 99999,
        settings: {
          rows: 1,
          dots: columns.length > 3,
          slidesToShow: columns.length > 3 ? 3 : columns.length,
          centerMode: false,
        },
      },
    ],
  };

  const testimonials = columns.map((column) => (
    <Testimonial key={column._key}>
      <ImageContainer>
        <Image
          objectFit="contain"
          {...column.image}
          style={{ height: '100%' }}
        />
      </ImageContainer>
      <ParagraphText>{column.paragraphText}</ParagraphText>
      {!!column.author && (
        <HeadingText size="label-lg">{column.author}</HeadingText>
      )}
      {!!column.authorJobTitle && (
        <JobTitle size="body-md">{column.authorJobTitle}</JobTitle>
      )}
    </Testimonial>
  ));

  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';

  return (
    <Section backgroundColour={backgroundColourToken} verticalPadding>
      <Content>
        <HeadingLevel>
          <HeadingContainer>
            {tag?.tagText && (
              <Tag
                bgColour={tag?.tagColour}
                margin="0 0 var(--spacing-xxx-small) 0"
              >
                {tag.tagText}
              </Tag>
            )}
            <CaptionHeadingDescription
              caption={caption}
              heading={heading}
              description={subheading}
              padding={{
                caption: 'x-small',
                captionMobile: 'x-small',
                descriptionMobile: 'xx-small',
              }}
              desktopAlignment="center"
              mobileAlignment="center"
            />
          </HeadingContainer>
          <SliderContainer>
            <SliderV2 settings={sliderSettings} slideItems={testimonials}>
              {testimonials}
            </SliderV2>
          </SliderContainer>
        </HeadingLevel>
      </Content>
    </Section>
  );
};

const Content = styled.div`
  position: relative;

  .slick-slide {
    display: flex !important;
    position: relative;
    padding-left: var(--spacing-small);
    &:first-of-type {
      padding-left: 0;
    }

    & > div {
      display: flex !important;
    }
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  ${Paragraph} {
    color: var(--text-strong);
    font-weight: var(--font-weight-regular);
  }
`;

const Testimonial = styled.div`
  position: relative;
  display: flex !important;
  flex-direction: column;
  text-align: center;
  background: var(--background-subtle);

  border: 2px solid var(--colour-border-subtle);
  padding: var(--spacing-250) var(--spacing-250) var(--spacing-250)
    var(--spacing-250);
  border-radius: var(--radius-l);
  margin: var(--spacing-25);
`;

const SliderContainer = styled.div`
  position: relative;
  z-index: 2;
  margin-top: var(--spacing-x-large);

  .slick-list {
    overflow: visible !important;
    display: flex !important;
  }

  & .slick-track {
    display: flex !important;
  }

  ${screen.sm} {
    & .slick-list {
      overflow: hidden !important;
    }
  }
`;

const ImageContainer = styled.div`
  height: 128px;

  ${screen.sm} {
    height: 144px;
  }
`;

const ParagraphText = styled(Paragraph)`
  margin-top: var(--spacing-small);
  margin-bottom: var(--spacing-250);
  flex-grow: 1;
`;

const HeadingText = styled(Paragraph)`
  margin-top: auto;
`;

const JobTitle = styled(Paragraph)`
  color: var(--text-strong);
`;

export { ImageTextColumns };
